import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useLocation } from "react-router";
import { useQueryClient } from '@tanstack/react-query';
// @YUp
import * as Yup from "yup";
import { Button, Grid, TextField, FormControl, MenuItem, Select, FormHelperText } from "@mui/material";
import API from "../../utils/Axios.utils";
import { Notification } from "../../utils/Notification";
import FIelds from "../../uicomponents/FIelds";
import TextArea from "../../uicomponents/TextArea";
import CustomSelect from "../../uicomponents/CustomSelect";
import CustomMultiSelect from "../../uicomponents/CustomMultiSelect";
import TextEditor from "../../utils/TextEditor";
import useAuth from "../../hooks/useAuth";

const AddJob = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const reqData = location?.state?.requisition;
  const [isFormSubmitted, setIsFormSubmitted] = React.useState(false);
  // Set once the job is created: keeps Submit locked during the post-success redirect delay
  const [jobCreated, setJobCreated] = React.useState(false);

  // Snackbar
  const [alert, setAlert] = React.useState(false);
  const [alertContent, setAlertContent] = React.useState("");
  const [alertType, setAlertType] = React.useState("");

  // AI Job Description Generator States
  const [aiKeywords, setAiKeywords] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiGeneratedContent, setAiGeneratedContent] = useState("");

  // Additive resume-scoring instructions — appended after the RAG service's own
  // base scoring prompt, never a replacement for it. Leaving this box empty
  // means the job scores exactly as it always has.
  const [scoringPromptText, setScoringPromptText] = useState("");
  // Recruiter-chosen label for the saved prompt itself — separate from the job
  // title, so two jobs sharing the same title (different clients/requirements)
  // still show up distinctly in the reuse dropdowns.
  const [promptName, setPromptName] = useState("");
  // The name this prompt currently has in the DB, as of the last time it was
  // loaded/picked — compared against promptName on submit to tell a pure
  // rename (safe to update in place) apart from picking a fresh one.
  const [originalPromptName, setOriginalPromptName] = useState("");
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [selectedSavedPromptId, setSelectedSavedPromptId] = useState("");

  const handleAlert = () => {
    setAlert(false);
  };

  const [editorContent, setEditorContent] = React.useState("");
  const [clearData, setClearData] = React.useState(false);
  // For Validation
  const [editorIsEmpty, setEditorIsEmpty] = React.useState(false);

  // Skill Data
  const [skillData, setSkillData] = useState([]);

  const handleEditorEmptyChange = (isEmpty) => {
    setEditorIsEmpty(isEmpty);
  };
  // validation code end

  const handleContentChange = (content) => {
    // Handle the editor content here (e.g., store it in state)
    setEditorContent(content);
  };

  const handleGenerateAIDescription = async (values) => {
    if (!values.title) {
      setAlertContent("Please enter a Job Title first to generate a description.");
      setAlertType("error");
      setAlert(true);
      return;
    }

    setIsGenerating(true);
    try {
      const payload = {
        title: values.title,
        keywords: aiKeywords || (values.techSkills && values.techSkills.length > 0 ? values.techSkills.join(', ') : '') || values.title
      };

      const res = await API.post("/api/job/generate-description", payload, config);
      const generatedHtml = res?.data?.description;

      if (generatedHtml) {
        setAiGeneratedContent(generatedHtml);
        setEditorContent(generatedHtml); // Update Formik/editor content state
        setAlertContent("AI Job Description generated successfully!");
        setAlertType("success");
        setAlert(true);
      } else {
        throw new Error("Invalid response received from server.");
      }
    } catch (error) {
      console.error("AI Generation Error:", error);
      setAlertContent(error?.response?.data?.message || error?.message || "Failed to generate job description.");
      setAlertType("error");
      setAlert(true);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateScoringPrompt = async (values) => {
    if (!values.title || !editorContent || !editorContent.replace(/<[^>]*>/g, '').trim()) {
      setAlertContent("Please fill in Job Title and Job Description first.");
      setAlertType("error");
      setAlert(true);
      return;
    }

    setIsGeneratingPrompt(true);
    try {
      const payload = {
        title: values.title,
        job_description: editorContent,
        techSkills: values.techSkills,
      };
      const res = await API.post("/api/job/generate-scoring-prompt", payload, config);
      setScoringPromptText(res?.data?.prompt_text || "");
      setSelectedSavedPromptId(""); // this is a fresh draft, not a saved entry
      // Default the name to the job title, same convenience default as the rest
      // of this feature — recruiter can still rename before saving.
      if (!promptName.trim()) {
        setPromptName(values.title);
      }
    } catch (error) {
      console.error("Scoring prompt generation error:", error);
      setAlertContent(error?.response?.data?.message || "Failed to generate scoring instructions.");
      setAlertType("error");
      setAlert(true);
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  // Shared by both reuse dropdowns (by Prompt Name and by Job Title) — they
  // list the same saved prompts, just labeled differently, and both drive this
  // same selectedSavedPromptId state, which is what keeps them in sync: picking
  // an entry in either one highlights the matching entry in the other too.
  const handleSelectSavedPrompt = (id, form) => {
    setSelectedSavedPromptId(id);
    if (!id) {
      return;
    }
    const picked = savedPrompts.find((p) => String(p.scoring_prompt_id) === String(id));
    const pickedName = picked?.prompt_name || picked?.title || "";
    setScoringPromptText(picked?.prompt_text || "");
    setPromptName(pickedName);
    setOriginalPromptName(pickedName);
    form.setFieldValue("scoring_prompt_id", id);
  };

  const handleScoringPromptTextChange = (value, form) => {
    setScoringPromptText(value);
    // Editing the actual instructions means this is now new/edited content,
    // not that exact saved entry — clear the link so submit forks a fresh row.
    // (Unlike a pure rename, this could change what the prompt means, so it
    // must never silently apply to other jobs still reusing the original row.)
    if (selectedSavedPromptId) {
      setSelectedSavedPromptId("");
      form.setFieldValue("scoring_prompt_id", "");
    }
  };

  const handlePromptNameChange = (value, form) => {
    // Renaming does NOT fork — it never changes the actual scoring
    // instructions, so it's safe to update the existing row's label in place
    // on submit (see originalPromptName comparison there). The link
    // (selectedSavedPromptId / scoring_prompt_id) stays intact.
    setPromptName(value);
  };

  const handlePrefillKeywords = (values) => {
    const titleVal = values.title || "";
    const techSkillsVal = (values.techSkills && values.techSkills.length > 0)
      ? values.techSkills.join(", ")
      : "";
    const combined = [titleVal, techSkillsVal].filter(Boolean).join(", ");
    setAiKeywords(combined);
  };

  // const handleClearButtonClick = () => {
  //   // Set clearData to true to clear the editor's content
  //   setClearData(true);
  // };

  const initialValues = {
    title: reqData?.title || "",
    location: reqData?.location || "",
    experience: reqData?.min_exp && reqData?.max_exp ? `${reqData.min_exp} - ${reqData.max_exp}` : (reqData?.min_exp || ""),
    currency: reqData?.currency || "",
    budget: (() => {
      const b = reqData?.budget || reqData?.max_budget || reqData?.min_budget || "";
      if (typeof b === "string" && b.includes("-")) {
        const parts = b.split("-").map((p) => parseFloat(p.trim())).filter((n) => !isNaN(n));
        return parts.length > 0 ? String(parts[parts.length - 1]) : "";
      }
      return b;
    })(),
    budget_uom: "",
    mode_of_work: reqData?.employment_type || "",
    notice_period: 30,
    // primary_skills: "",
    job_description: reqData?.job_description || "",
    status: "Active",
    notes: reqData?.requisition_id ? `Created from Requisition ${reqData.requisition_id}` : "",
    coreSkillId: "",      // NEW
    techSkills: [],
    client_id: reqData?.client_id || '',
    no_of_requirements: reqData?.no_of_requirements || reqData?.headcount || reqData?.no_of_people || 1,
    fulfilled_count: 0,
    contact_id: '',
    background_verification: 'No',
    assignment_duration_days: 90,
    pipeline_type: '',
    voice_bot_id: '',
    db_customer_name: '',
    campaign_name: '',
    organisation_name: '',
    customer_name: '',
    processes: '',
    module_name: '',
    scoring_prompt_id: '',
  };

  const { token } = useAuth();

  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  // const onSubmit = async (values) => {
  //   setIsFormSubmitted(true);
  //   if (editorIsEmpty) {
  //     // setIsFormSubmitted(true);
  //     return;
  //   } else {
  //     values.job_description = editorContent;
  //     try {
  //       const res = await API.post("/api/job/addjob", values, config);
  //       setAlertContent(res?.data?.message);
  //       setAlertType("success");
  //       setAlert(true);
  //       setTimeout(() => {
  //         navigate("/jobs");
  //       }, 1500);
  //     } catch (error) {
  //       setAlertContent(error?.response?.data?.message);
  //       setAlertType("error");
  //       setAlert(true);
  //     }
  //   }
  // };

  const onSubmit = async (values, { setSubmitting }) => {
    if (jobCreated) return; // already created; redirect is pending
    setIsFormSubmitted(true);
    if (editorIsEmpty || !editorContent || !editorContent.trim()) {
      setSubmitting(false);
      return;
    } else {
      const locationsArray = values.locations
        ? values.locations.split(',').map(loc => loc.trim()).filter(loc => loc)
        : [];
      values.job_description = editorContent;

      const payload = {
        ...values,
        background_verification: values.background_verification === 'Yes' ? 'Yes' : 'No',
        location: locationsArray,  // ✅ Sends: ["Pune", "Mumbai"]
        job_description: editorContent,
      };

      try {
        // Additive resume-scoring instructions: save new/edited text as its own
        // reusable row first, then link the job to it. A saved prompt picked
        // as-is already carries its id (values.scoring_prompt_id); an empty box
        // means this job scores exactly as it always has (no id sent at all).
        if (scoringPromptText.trim() && !values.scoring_prompt_id) {
          try {
            const promptRes = await API.post(
              "/api/job/scoring-prompts",
              {
                title: values.title,
                prompt_name: promptName.trim() || values.title,
                prompt_text: scoringPromptText.trim(),
                source: selectedSavedPromptId ? "reused" : "generated",
              },
              config
            );
            payload.scoring_prompt_id = promptRes?.data?.scoring_prompt_id;
          } catch (promptError) {
            console.error("Failed to save scoring instructions:", promptError);
            // Never block job creation over this — it's an enrichment, not a
            // requirement. The job simply gets no linked prompt.
            delete payload.scoring_prompt_id;
          }
        } else if (
          values.scoring_prompt_id &&
          promptName.trim() &&
          promptName.trim() !== originalPromptName
        ) {
          // Link intact, instructions untouched, only the name changed —
          // update the existing row in place instead of forking a new one.
          try {
            await API.put(
              `/api/job/scoring-prompts/${values.scoring_prompt_id}`,
              { prompt_name: promptName.trim() },
              config
            );
          } catch (renameError) {
            console.error("Failed to rename scoring prompt:", renameError);
            // Non-blocking — the job still saves and links correctly either way.
          }
        }
        if (!payload.scoring_prompt_id) {
          delete payload.scoring_prompt_id;
        }

        const res = await API.post("/api/job/addjob", payload, config);

        const { message, showAlert, alertType, fulfillment } = res?.data || {};

        // Build final message for Notification
        let finalMessage = message || "Job created successfully.";
        if (showAlert && alertType === "FULL_FILLED" && fulfillment) {
          finalMessage = `${message} All ${fulfillment.total} positions are filled.`;
        }

        // Invalidate React Query cache to refresh job list
        queryClient.invalidateQueries({ queryKey: ['jobs'] });

        // If converting from Requisition, update requisition status to job_created
        if (reqData?.id) {
          try {
            await API.put(`/api/requisitions/${reqData.id}/status`, { status: 'job_created' }, config);
          } catch (reqErr) {
            console.error("Failed to update requisition status to job_created:", reqErr);
          }
        }

        setJobCreated(true);
        setAlertContent(finalMessage);
        setAlertType("success");
        setAlert(true);

        setTimeout(() => {
          navigate("/jobs");
        }, 800);
      } catch (error) {
        setAlertContent(error?.response?.data?.message || "Something went wrong");
        setAlertType("error");
        setAlert(true);
        setSubmitting(false);
      }
    }
  };

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .required("Job title is required.")
      .min(2, "Minimum 2 characters are required.")
      .max(100, "Maximum length is 100 characters.")
      .test(
        "no-empty-or-spaces",
        "Title must not be empty or contain only spaces.",
        (value) => value?.trim().length > 0
      )
      .test(
        "no-leading-trailing-spaces",
        "Leading and trailing spaces are not allowed.",
        (value) => value === value?.trim()
      )
      .matches(
        /^[a-zA-Z0-9.,#\-_ +/&()]+$/,
        "Please use only letters, numbers, spaces, and symbols: . , # - _ + / & ( )"
      ),
    // location: Yup.string()
    //   .min(2, "Minimum 2 characters is required.")
    //   .max(50, "Maximum length is 100 characters.")
    //   .matches(
    //     /^[a-zA-Z]+(\s[a-zA-Z]+)*$/,
    //     "Invalid Location format.Spaces at the start and end are not allowed."
    //   )
    //   .nullable().notRequired(),
    locations: Yup.string()
      .nullable().notRequired()
      .test("valid-locations", "Invalid location format", function (value) {
        if (!value || !value.trim()) return true;

        // ✅ Split and validate each location using OLD regex
        const locations = value.split(',').map(loc => loc.trim()).filter(Boolean);

        // Check each location matches old regex pattern
        const locationRegex = /^[a-zA-Z]+(\s[a-zA-Z]+)*$/;
        const isValid = locations.every(loc =>
          loc.length >= 2 &&
          loc.length <= 50 &&
          locationRegex.test(loc)
        );

        return isValid && locations.length > 0;
      }),
    experience: Yup.string()
      .transform(value => value === '' ? null : value)
      .min(1, "Minimum 1 character is required.")
      .max(20, "Maximum length is 20 characters.")
      .nullable().notRequired(),
    currency: Yup.string().nullable().notRequired(),
    budget: Yup.string().nullable().notRequired(),
    budget_uom: Yup.string().nullable().notRequired(),
    mode_of_work: Yup.string().nullable().notRequired(),
    notice_period: Yup.string()
      .required("Timeline (Days) is required.")
      .matches(/^\d+$/, "Must be numbers only (days)")
      .test("timeline-range", "Timeline must be between 1 and 365 days.", (value) => !value || (Number(value) >= 1 && Number(value) <= 365)),
    assignment_duration_days: Yup.number()
      .transform((value, originalValue) => originalValue === '' ? null : value)
      .min(1, "Minimum 1 day")
      .max(3650, "Maximum 10 years (3650 days)")
      .nullable().notRequired(),
    // job_description: Yup.string()
    //   .min(2, "Minimum 2 character is required.")
    //   .max(100, "Maximum length is 100 characters.")
    //   .nullable().notRequired(),
    status: Yup.string().nullable().notRequired(),
    notes: Yup.string()
      .transform(value => value === '' ? null : value)
      .min(2, "Minimum 2 characters is required.")
      .max(255, "Maximum length is 255 characters.")
      .nullable().notRequired(),
    // .nullable().notRequired(),
    // primary_skills: Yup.array().nullable().notRequired(),
    coreSkillId: Yup.number()
      .transform((value, originalValue) => originalValue === '' ? null : value)
      .typeError("This field is required.")
      .nullable().notRequired(),
    techSkills: Yup.array().nullable().notRequired(),
    client_id: Yup.number()
      .typeError("This field is required")
      .required("Client is required"),
    contact_id: Yup.number()
      .transform((value, originalValue) => originalValue === '' ? null : value)
      .nullable()
      .when("client_id", {
        is: (client_id) => client_id,
        then: (schema) => schema.nullable().notRequired(),
        otherwise: (schema) => schema.notRequired()
      }),
    no_of_requirements: Yup.number()
      .transform((value, originalValue) => originalValue === '' ? null : value)
      .typeError("Numbers of Requirements is required.")
      .required("Numbers of Requirements is required.")
      .min(1, "Minimum 1 requirement"),
    contact_person_name: Yup.string().max(50, "Max 50 characters"),
    contact_person_number: Yup.string()
      .transform(value => value === '' ? null : value)
      .nullable().notRequired()
      .matches(/^[\+]?[1-9][\d]{0,15}$/, "Invalid phone number"),
    contact_person_email: Yup.string()
      .transform(value => value === '' ? null : value)
      .nullable().notRequired()
      .email("Invalid email"),
    fulfilled_count: Yup.number()
      .transform((value, originalValue) => originalValue === '' ? null : value)
      .typeError("Filled Positions is required.")
      .required("Filled Positions is required.")
      .min(0, "Cannot be negative")
      .when("status", {
        is: (val) => ["Partially-Fulfilled", "Full-Filled"].includes(val),
        then: (schema) =>
          schema
            .nullable().notRequired()
            .test(
              "not-greater-than-req",
              "Filled positions cannot exceed total requirements",
              function (value) {
                const { no_of_requirements } = this.parent;
                if (!no_of_requirements) return true;
                if (value == null) return false;
                return value <= no_of_requirements;
              }
            ),
        otherwise: (schema) => schema.notRequired(),
      })
      .when("status", {
        is: (val) => val === "Partially-Fulfilled",
        then: (schema) =>
          schema.test(
            "partial-range",
            "Filled positions is out of range for Partially Filled status",
            function (value) {
              const { no_of_requirements } = this.parent;
              if (!no_of_requirements || value == null) return true;
              if (no_of_requirements < 2) return this.createError({ message: "Partially-Fulfilled needs at least 2 requirements" });
              if (value < 1) return this.createError({ message: "Filled positions must be at least 1 for Partially Filled status" });
              if (value >= no_of_requirements) return this.createError({ message: "Filled positions must be less than total requirements for Partially Filled status" });
              return true;
            }
          ),
        otherwise: (schema) => schema,
      }),
    background_verification: Yup.string().oneOf(['Yes', 'No']).notRequired(),
    pipeline_type: Yup.string()
      .oneOf(['resume_based', 'non_resume_based'], 'Please select a valid job type')
      .required('Job type is required'),
  });
  const [coreSkills, setCoreSkills] = useState([]);
  const othersSkill = coreSkills.find(s => s.label.trim().toLowerCase() === 'others') ?? null;

  const [techSkills, setTechSkills] = useState([]);

  const [clients, setClients] = useState([]);
  const [clientContacts, setClientContacts] = useState([]);

  const getCoreSkills = async () => {
    try {
      const res = await API.get(
        "/api/core-skill/coreSkills",
        config
      );
      const formattedCore = res?.data?.coreSkills?.map((item) => ({
        id: item.id,
        value: item.id,
        label: item.name,
      }));
      setCoreSkills(formattedCore);
    } catch (error) {
      console.log("getCoreSkills error", error);
    }
  };

  const getTechSkillsByCore = async (coreSkillId) => {

    if (!coreSkillId) {
      setTechSkills([]);
      return;
    }
    try {
      const res = await API.get(
        `/api/tech-skill/alltechSkills/${coreSkillId}`,
        config
      );
      const formattedTech = res?.data?.techSkills?.map((item) => ({
        id: item.id,
        value: item.name,
        label: item.name,
      }));
      const allSkillsOption = [{
        id: 'all',
        value: 'All Skills',
        label: 'All Skills'
      }];
      setTechSkills([...allSkillsOption, ...formattedTech]);
      setTechSkills(formattedTech);
    } catch (error) {
      console.log("getTechSkillsByCore error", error);
    }
  };
  // useEffect(()=>{
  //   getTechSkillsByCore(coreSkillId);
  // },[coreSkillId])
  const getSkills = async () => {
    let url = `/api/skill/skill_list`;

    try {
      const res = await API.get(url, config);
      const formattedSkills = res?.data?.skill?.map((skill) => ({
        id: skill.skill_id,
        value: skill.skill_name,
        label: skill.skill_name,
      }));
      setSkillData(formattedSkills);
    } catch (error) {
      console.log("error", error);
    }
  };

  const getClients = async () => {
    try {
      const res = await API.get('/api/client/active_client_list', config);
      const formattedClients = res?.data?.clients?.map(item => ({
        id: item.client_id,
        value: item.client_id,
        label: `${item.name}`,
        client_code: item.client_code,
        name: item.name
      }));
      setClients(formattedClients);
    } catch (error) {
      console.log('getCustomers error:', error);
    }
  };
  const getClientContacts = async (clientId) => {
    if (!clientId) {
      setClientContacts([]);
      // setFieldValue('contact_id', ''); // Reset in formik
      return;
    }

    try {
      const res = await API.get(`/api/client/ClientsContacts/${clientId}`, config);
      const formattedContacts = res?.data?.contacts?.map(item => ({
        id: item.contact_id,
        value: item.contact_id,
        label: `${item.name || 'N/A'}`,
        name: item.name,
        email: item.email,
        phone: item.phone
      })) || [];
      setClientContacts(formattedContacts);
    } catch (error) {
      console.log('getClientContacts error', error);
      setClientContacts([]);
    }
  };
  const getSavedScoringPrompts = async () => {
    try {
      const res = await API.get("/api/job/scoring-prompts", config);
      setSavedPrompts(res?.data?.prompts || []);
    } catch (error) {
      console.log("getSavedScoringPrompts error", error);
    }
  };

  useEffect(() => {
    getSkills();
    getCoreSkills();
    getClients();
    getSavedScoringPrompts();
    if (reqData?.job_description) {
      setEditorContent(reqData.job_description);
    }
    if (reqData?.client_id) {
      getClientContacts(reqData.client_id);
    }
  }, []);

  const navigateToPreviousPage = () => {
    navigate(-1);
  };

  return (
    <div style={{ marginTop: 20 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={navigateToPreviousPage}>
        Back
      </Button>
      <div
        style={{
          border: "1px solid #989898",
          borderRadius: "10px",
          padding: "15px",
        }}
      >
        <div
          style={{
            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            fontSize: "20px",
            fontWeight: "500",
            letterSpacing: 1,
            textAlign: "left",
          }}
        >
          <span>Create Job</span>
          <div style={{ display: "grid" }}>
            <Formik
              initialValues={initialValues}
              enableReinitialize={true}
              onSubmit={onSubmit}
              validationSchema={validationSchema}
              validateOnMount={true}
            >
              {(props) => (
                <Form noValidate>
                  <Grid container spacing={2}>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Job Title <span style={{ color: 'red' }}>*</span></span>

                      <Field
                        as={TextField}
                        name="title"
                        fullWidth
                        error={props.errors.title && props.touched.title}
                        size="small"
                        helperText={<ErrorMessage name="title" />}
                        required
                      />
                    </Grid>
                    {/* Customer Dropdown - ADD THIS BEFORE Currency */}
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>Client <span style={{ color: 'red' }}>*</span></span>
                      <Field name="client_id" >
                        {({ field, form }) => (
                          <FormControl fullWidth variant="outlined" size="small" error={form.touched.client_id && Boolean(form.errors.client_id)}>
                            <Select
                              {...field}
                              // onChange={(event) => {
                              //   const value = event.target.value;
                              //   form.setFieldValue('client_id', value);
                              // }}
                              onChange={(event) => {
                                const value = event.target.value;
                                form.setFieldValue('client_id', value);
                                getClientContacts(value);  // ✅ NEW: Fetch contacts
                                form.setFieldValue('contact_id', ''); // ✅ Reset contact
                              }}
                              displayEmpty
                            >
                              {clients.map((option) => (
                                <MenuItem key={option.id} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText sx={{ color: 'red' }}>
                              <ErrorMessage name="client_id" />
                            </FormHelperText>
                          </FormControl>
                        )}
                      </Field>
                    </Grid>
                    {/* ✅ ADD CONTACT PERSON DROPDOWN - AFTER Customer Grid */}
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>
                        Contact Person 
                      </span>
                      <Field name="contact_id">
                        {({ field, form }) => (
                          <FormControl
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={form.touched.contact_id && Boolean(form.errors.contact_id)}>
                            <Select
                              {...field}
                              value={field.value || ''}
                              onChange={(event) => {
                                const value = event.target.value;
                                form.setFieldValue('contact_id', value);
                              }}
                              displayEmpty>
                              {clientContacts.map((option) => (
                                <MenuItem key={option.id} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText sx={{ color: 'red' }}>
                              <ErrorMessage name="contact_id" />
                            </FormHelperText>
                          </FormControl>
                        )}
                      </Field>
                    </Grid>
                    {/* Job Type */}
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>Job Type <span style={{ color: 'red' }}>*</span></span>
                      <Field name="pipeline_type">
                        {({ field, form }) => (
                          <FormControl fullWidth variant="outlined" size="small" error={form.touched.pipeline_type && Boolean(form.errors.pipeline_type)}>
                            <Select
                              {...field}
                              value={field.value || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                form.setFieldValue('pipeline_type', value);
                                if (value === 'non_resume_based') {
                                  form.setFieldValue('coreSkillId', othersSkill ? othersSkill.value : '');
                                  form.setFieldValue('techSkills', []);
                                } else {
                                  form.setFieldValue('coreSkillId', '');
                                  form.setFieldValue('techSkills', []);
                                }
                              }}
                              displayEmpty
                            >
                              <MenuItem value="" disabled><em>Select Job Type</em></MenuItem>
                              <MenuItem value="resume_based">White Collar (Resume Required)</MenuItem>
                              <MenuItem value="non_resume_based">Blue Collar (No Resume)</MenuItem>
                            </Select>
                            <FormHelperText sx={{ color: 'red' }}>
                              <ErrorMessage name="pipeline_type" />
                            </FormHelperText>
                            {props.values.pipeline_type === 'non_resume_based' && !othersSkill && (
                              <FormHelperText sx={{ color: '#ed6c02' }}>
                                "Others" skill not found in system. Please select a Core Skill manually.
                              </FormHelperText>
                            )}
                          </FormControl>
                        )}
                      </Field>
                    </Grid>
                    {/* Location */}
                    {/* <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Location</span>
                      <FIelds name="location" props={props} />
                    </Grid> */}
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>
                        Locations 
                      </span>
                      <Field name="locations">
                        {({ field, form, meta }) => {
                          // ✅ Convert for preview
                          const locationArray = field.value
                            ? field.value.split(',').map(loc => loc.trim()).filter(Boolean)
                            : [];

                          return (
                            <>
                              <TextField
                                {...field}
                                fullWidth
                                error={meta.touched && meta.error}
                                size="small"
                                helperText={
                                  meta.touched && meta.error ? (
                                    <ErrorMessage name="locations" />
                                  ) : (
                                    "Enter multiple locations separated by commas"
                                  )
                                }
                              />
                            </>
                          );
                        }}
                      </Field>
                    </Grid>

                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Experience </span>
                      <FIelds name="experience" props={props} onInput="yes" />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Currency </span>
                      <CustomSelect
                        name="currency"
                        props={props}
                        options={allCurrency}
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Budget </span>
                      <FIelds name="budget" props={props} onInput="yes" />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Budget UOM </span>
                      <CustomSelect
                        name="budget_uom"
                        props={props}
                        options={budgetUOM}
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Work Mode </span>
                      <CustomSelect
                        name="mode_of_work"
                        props={props}
                        options={WorkMode}
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>
                        Timeline (Days) <span style={{ color: 'red' }}>*</span>
                      </span>
                      <FIelds
                        name="notice_period"
                        props={props}
                        onInput="yes"
                      />
                    </Grid>
                    {/* ✅ ADD after Notice Period Grid, before Core Skill */}
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>
                        Assignment Duration (Days) 
                      </span>
                      <Field
                        as={TextField}
                        name="assignment_duration_days"
                        type="number"
                        fullWidth
                        error={props.errors.assignment_duration_days && props.touched.assignment_duration_days}
                        size="small"
                        helperText={<ErrorMessage name="assignment_duration_days" />}
                        required
                        inputProps={{
                          min: 1,
                          max: 3650,
                          placeholder: "90"
                        }}
                      />
                    </Grid>
                    {/* <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Primary Skills</span>
                      <CustomMultiSelect
                        name="primary_skills"
                        props={props}
                        options={skillData}
                      />
                    </Grid> */}
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Core Skill </span>
                      <Field name="coreSkillId">
                        {({ field, form }) => (
                          <FormControl
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={form.touched.coreSkillId && Boolean(form.errors.coreSkillId)}
                          >
                            <Select
                              {...field}
                              disabled={props.values.pipeline_type === 'non_resume_based' && !!othersSkill}
                              onChange={(event) => {
                                const value = event.target.value;
                                form.setFieldValue("coreSkillId", value);
                                form.setFieldValue("techSkills", []); // reset multi-select
                                getTechSkillsByCore(value); // call your fetch function
                              }}
                              displayEmpty
                            >
                              {coreSkills.map((option) => (
                                <MenuItem key={option.id} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText sx={{ color: "red" }}>
                              <ErrorMessage name="coreSkillId" />
                            </FormHelperText>
                          </FormControl>
                        )}
                      </Field>
                    </Grid>
                    {/* <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Tech Skills</span>
                      <Field name="techSkills">
                        {({ field, form }) => (
                          <FormControl
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={form.touched.techSkills && Boolean(form.errors.techSkills)}
                          >
                            <Select
                              multiple
                              displayEmpty
                              {...field}
                              value={field.value || []}
                              onChange={(event) => {
                                form.setFieldValue("techSkills", event.target.value);
                              }}
                              onBlur={field.onBlur}
                              size="small"
                              variant="outlined"
                            >
                              {techSkills.map((option) => (
                                <MenuItem key={option.id} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText sx={{ color: "red" }}>
                              <ErrorMessage name="techSkills" />
                            </FormHelperText>
                          </FormControl>
                        )}
                      </Field>
                    </Grid> */}
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Tech Skills</span>
                      <Field name="techSkills">
                        {({ field, form }) => {
                          const handleChange = (event) => {
                            const selectedValues = event.target.value || [];

                            // ✅ "All Skills" LOGIC
                            if (selectedValues.includes('All Skills') ||
                              selectedValues.some(val => val === 'All Skills')) {
                              // Select ALL available tech skills
                              const allTechSkills = techSkills
                                .filter(skill => skill.value !== 'All Skills')
                                .map(skill => skill.value);
                              form.setFieldValue("techSkills", allTechSkills);
                            } else {
                              form.setFieldValue("techSkills", selectedValues);
                            }
                          };

                          return (
                            <FormControl
                              fullWidth
                              variant="outlined"
                              size="small"
                              error={form.touched.techSkills && Boolean(form.errors.techSkills)}
                            >
                              <Select
                                multiple
                                displayEmpty
                                {...field}
                                disabled={props.values.pipeline_type === 'non_resume_based'}
                                value={field.value || []}
                                onChange={handleChange}
                                onBlur={field.onBlur}
                                size="small"
                                variant="outlined"
                                renderValue={(selected) => (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 6,
                                      maxHeight: 60,        // ✅ Prevents stretching
                                      overflowY: "auto"     // ✅ Scroll instead of grow
                                    }}
                                  >
                                    {selected.map((value) => (
                                      <span
                                        key={value}
                                        style={{
                                          background: "#E3F2FD",
                                          padding: "4px 8px",
                                          borderRadius: 12,
                                          fontSize: "13px",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {value}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              >
                                <MenuItem value="All Skills">
                                  All Skills
                                </MenuItem>
                                {techSkills
                                  .filter(skill => skill.value !== 'All Skills')  // ✅ Exclude from list
                                  .map((option) => (
                                    <MenuItem key={option.id} value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                              </Select>
                              <FormHelperText sx={{ color: "red" }}>
                                <ErrorMessage name="techSkills" />
                              </FormHelperText>
                            </FormControl>
                          );
                        }}
                      </Field>
                    </Grid>

                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Status </span>
                      <Field name="status">
                        {({ field, form }) => (
                          <FormControl fullWidth variant="outlined" size="small" error={form.touched.status && Boolean(form.errors.status)}>
                            <Select
                              {...field}
                              onChange={(e) => {
                                const newStatus = e.target.value;
                                form.setFieldValue("status", newStatus);
                                // ✅ Reset filled positions if status is not fulfilled
                                if (!["Full-Filled", "Partially-Fulfilled"].includes(newStatus)) {
                                  form.setFieldValue("fulfilled_count", 0);
                                }
                              }}
                            >
                              {allStatus.map((option) => (
                                <MenuItem key={option.id} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText sx={{ color: "red" }}>
                              <ErrorMessage name="status" />
                            </FormHelperText>
                          </FormControl>
                        )}
                      </Field>
                    </Grid>
                    {/* ✅ ADD BGV CHECKBOX - After Work Mode, before Notice Period */}
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>BGV</span>
                      <Field name="background_verification">
                        {({ field, form, meta }) => (
                          <FormControl
                            fullWidth
                            error={meta.touched && Boolean(meta.error)}
                            size="small">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <input
                                type="checkbox"
                                checked={field.value == 'Yes'}
                                onChange={(e) => form.setFieldValue('background_verification', e.target.checked ? 'Yes' : 'No')}
                                style={{
                                  width: '18px',
                                  height: '18px',
                                  accentColor: '#2777AA'
                                }}
                              />
                              <span style={{ fontSize: '16px', fontWeight: 500 }}>
                                Background Verification
                              </span>
                            </div>
                            {meta.touched && meta.error && (
                              <FormHelperText sx={{ color: 'red', marginLeft: '26px' }}>
                                {meta.error}
                              </FormHelperText>
                            )}
                          </FormControl>
                        )}
                      </Field>
                    </Grid>

                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>Numbers of Requirements <span style={{ color: 'red' }}>*</span></span>
                      <Field as={TextField}
                        name="no_of_requirements"
                        type="number"
                        fullWidth
                        error={props.errors.no_of_requirements && props.touched.no_of_requirements}
                        size="small"
                        helperText={<ErrorMessage name="no_of_requirements" />}
                        required
                        inputProps={{ min: 1, max: 100 }}
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>
                        Filled Positions{' '}
                        <span style={{ color: 'red' }}>*</span>
                      </span>
                      <Field
                        as={TextField}
                        name="fulfilled_count"
                        type="number"
                        fullWidth
                        disabled={!['Full-Filled', 'Partially-Fulfilled'].includes(props.values.status)}
                        error={props.errors.fulfilled_count && props.touched.fulfilled_count}
                        size="small"
                        helperText={
                          props.errors.fulfilled_count && props.touched.fulfilled_count
                            ? <ErrorMessage name="fulfilled_count" />
                            : props.values.status === 'Full-Filled'
                            ? `Enter ${props.values.no_of_requirements} to match total requirements`
                            : props.values.status === 'Partially-Fulfilled'
                            ? ((props.values.no_of_requirements || 1) - 1 < 1
                              ? 'Partially-Fulfilled needs at least 2 requirements'
                              : `Enter a value between 1 and ${(props.values.no_of_requirements || 1) - 1}`)
                            : ''
                        }
                        inputProps={{ min: 0 }}
                      />
                      {/* Conditional Alerts for Add Job */}
                      {props.values.status === 'Full-Filled' && (
                        <div style={{
                          marginTop: '6px', padding: '7px 12px', background: '#e8f5e9', border: '1px solid #a5d6a7',
                          borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#1b5e20', fontWeight: 500
                        }}>
                          ℹ️ Filled Positions must equal {props.values.no_of_requirements}
                        </div>
                      )}
                      {props.values.status === 'Partially-Fulfilled' && (
                        <div style={{
                          marginTop: '6px', padding: '7px 12px', background: '#e3f2fd', border: '1px solid #90caf9',
                          borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#0d47a1', fontWeight: 500
                        }}>
                          ℹ️ {(props.values.no_of_requirements || 1) - 1 < 1
                            ? 'Partially-Fulfilled needs at least 2 requirements. Use Active or Full-Filled.'
                            : `Enter a value between 1 and ${(props.values.no_of_requirements || 1) - 1}`}
                        </div>
                      )}
                    </Grid>
                    {/* <Grid item xl={4} lg={4} md={6} sm={12} xs={12}></Grid> */}
                    {/* <Grid item xl={4} lg={4} md={6} sm={12} xs={12}></Grid> */}
                    {/* <Grid item xl={4} lg={4} md={6} sm={12} xs={12}></Grid> */}
                    <Grid item xl={8} lg={8} md={8} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Notes</span>
                      <Field
                        as={TextField}
                        name={"notes"}
                        multiline
                        rows={5}
                        fullWidth

                        size="small"

                      />                    </Grid>
                    {/* Voice Bot Configuration */}
                    <Grid item xs={12}>
                      <div style={{ borderTop: '1px solid #e0e0e0', margin: '8px 0 4px 0' }} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Voice Bot Configuration
                      </span>
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>Voice Bot ID</span>
                      <Field as={TextField} name="voice_bot_id" fullWidth size="small" placeholder="e.g. 21" />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>Campaign Name</span>
                      <Field as={TextField} name="campaign_name" fullWidth size="small" placeholder="e.g. shyena_hiring" />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>DB Customer Name</span>
                      <Field as={TextField} name="db_customer_name" fullWidth size="small" placeholder="e.g. shyena_hiring_agent" />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>Processes</span>
                      <Field as={TextField} name="processes" fullWidth size="small" placeholder="e.g. Intern Hiring" />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>Organisation Name</span>
                      <Field as={TextField} name="organisation_name" fullWidth size="small" placeholder="e.g. Shyena Tech Yarns" />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>Customer Name</span>
                      <Field as={TextField} name="customer_name" fullWidth size="small" placeholder="e.g. Acme Corp" />
                    </Grid>
                    {/* Training Simulation Configuration */}
                    <Grid item xs={12}>
                      <div style={{ borderTop: '1px solid #e0e0e0', margin: '8px 0 4px 0' }} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Training Simulation Configuration
                      </span>
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>Module Name</span>
                      <Field as={TextField} name="module_name" fullWidth size="small" placeholder="e.g. Intern Training Module" />
                    </Grid>
                    {/* AI Job Description Assistant UI Panel */}
                    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                      <div style={{
                        background: "#F8FAFC",
                        border: "1px solid #E2E8F0",
                        borderRadius: "12px",
                        padding: "20px",
                        marginBottom: "10px",
                        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)"
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                          <span style={{ fontSize: "16px", fontWeight: "600", color: "#1E293B" }}>
                            ✨ AI Job Description Assistant
                          </span>
                          <span style={{
                            fontSize: "11px",
                            background: "#EFF6FF",
                            color: "#2563EB",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            fontWeight: "600"
                          }}>
                            {/* Powered by Gemini */}
                          </span>
                        </div>
                        
                        <p style={{ fontSize: "13px", color: "#64748B", margin: "0 0 16px 0", lineHeight: "1.5" }}>
                          Type customized keywords (e.g. Python, Django, Senior) or click <strong>Pre-fill</strong> to fetch from current form inputs to generate a professional HTML job description.
                        </p>

                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={8}>
                            <TextField
                              placeholder="Keywords (e.g., Python, AWS, 5 years experience, Remote)"
                              size="small"
                              fullWidth
                              value={aiKeywords}
                              onChange={(e) => setAiKeywords(e.target.value)}
                              sx={{ backgroundColor: "#FFFFFF" }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4} style={{ display: "flex", gap: "10px" }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handlePrefillKeywords(props.values)}
                              sx={{
                                borderColor: "#2777AA",
                                color: "#2777AA",
                                textTransform: "none",
                                height: "38px",
                                whiteSpace: "nowrap",
                                flexGrow: 1
                              }}
                            >
                              Pre-fill
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleGenerateAIDescription(props.values)}
                              disabled={isGenerating}
                              sx={{
                                background: "#2777AA",
                                "&:hover": { background: "#1B5D88" },
                                textTransform: "none",
                                height: "38px",
                                whiteSpace: "nowrap",
                                flexGrow: 1
                              }}
                            >
                              {isGenerating ? "Generating..." : "Generate AI"}
                            </Button>
                          </Grid>
                        </Grid>
                      </div>
                    </Grid>

                    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Job Description <span style={{ color: 'red' }}>*</span></span>
                      {/* <TextArea TextArea name="job_description" props={props} /> */}
                      <TextEditor
                        getContent={handleContentChange}
                        setClearData={setClearData}
                        clearData={clearData}
                        initialContent={aiGeneratedContent}
                        onEditorEmptyChange={handleEditorEmptyChange}
                      />

                      {isFormSubmitted && editorIsEmpty && (
                        <div
                          style={{
                            color: "#d32f2f",
                            marginLeft: "14px",
                            fontWeight: 400,
                            fontSize: "0.75rem",
                          }}
                        >
                          This Field is required
                        </div>
                      )}
                    </Grid>

                    {/* Additive Resume-Scoring Instructions — appended after the RAG
                        service's own base scoring prompt, never a replacement for it.
                        Leaving this blank means the job scores exactly as it always has. */}
                    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                      <div style={{
                        background: "#F8FAFC",
                        border: "1px solid #E2E8F0",
                        borderRadius: "12px",
                        padding: "20px",
                        marginBottom: "10px",
                        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)"
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                          <span style={{ fontSize: "16px", fontWeight: "600", color: "#1E293B" }}>
                            🎯 Customize The Prompt 
                          </span>
                        </div>

                        <p style={{ fontSize: "13px", color: "#64748B", margin: "0 0 16px 0", lineHeight: "1.5" }}>
                          This job automatically inherits the AI scoring prompt configured for this Client/Organization in <strong>Prompt Management</strong>.
                          Optionally pick or write custom role-specific notes below to append on top, or leave blank to score using standard rules.
                        </p>

                        <Grid container spacing={2} alignItems="center">
                          {/* Two dropdowns, same saved-prompts list, different label to search
                              by. Both are driven by the same selectedSavedPromptId, so picking
                              one automatically highlights the matching entry in the other. */}
                          <Grid item xs={12} md={6}>
                            <span style={{ fontSize: "13px", color: "#64748B" }}>By Prompt Name</span>
                            <FormControl fullWidth variant="outlined" size="small">
                              <Select
                                value={selectedSavedPromptId}
                                displayEmpty
                                onChange={(e) => handleSelectSavedPrompt(e.target.value, props)}
                                sx={{ backgroundColor: "#FFFFFF" }}
                              >
                                <MenuItem value="">
                                  <em>Select by prompt name…</em>
                                </MenuItem>
                                {savedPrompts.map((p) => (
                                  <MenuItem key={p.scoring_prompt_id} value={p.scoring_prompt_id}>
                                    {p.prompt_name || p.title}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <span style={{ fontSize: "13px", color: "#64748B" }}>By Job Title</span>
                            <FormControl fullWidth variant="outlined" size="small">
                              <Select
                                value={selectedSavedPromptId}
                                displayEmpty
                                onChange={(e) => handleSelectSavedPrompt(e.target.value, props)}
                                sx={{ backgroundColor: "#FFFFFF" }}
                              >
                                <MenuItem value="">
                                  <em>Select by job title…</em>
                                </MenuItem>
                                {savedPrompts.map((p) => (
                                  <MenuItem key={p.scoring_prompt_id} value={p.scoring_prompt_id}>
                                    {p.title}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={8}>
                            <span style={{ fontSize: "13px", color: "#64748B" }}>Prompt Name</span>
                            <TextField
                              placeholder="e.g. Python Developer — Python + Azure"
                              fullWidth
                              size="small"
                              value={promptName}
                              onChange={(e) => handlePromptNameChange(e.target.value, props)}
                              sx={{ backgroundColor: "#FFFFFF" }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4} style={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleGenerateScoringPrompt(props.values)}
                              disabled={isGeneratingPrompt}
                              sx={{
                                borderColor: "#2777AA",
                                color: "#2777AA",
                                textTransform: "none",
                                height: "38px",
                                whiteSpace: "nowrap",
                                marginTop: "20px",
                              }}
                            >
                              {isGeneratingPrompt ? "Generating..." : "Generate from Job Details"}
                            </Button>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              placeholder="e.g. Prioritize AWS certification as a must-have; weight leadership experience heavily for this senior role…"
                              multiline
                              minRows={3}
                              fullWidth
                              size="small"
                              value={scoringPromptText}
                              onChange={(e) => handleScoringPromptTextChange(e.target.value, props)}
                              sx={{ backgroundColor: "#FFFFFF" }}
                            />
                          </Grid>
                        </Grid>
                      </div>
                    </Grid>

                    {/* Submit */}
                    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Button
                          variant="contained"
                          aria-haspopup="true"
                          style={{
                            background: jobCreated || props.isSubmitting || !props.isValid || !editorContent || !editorContent.replace(/<[^>]*>/g, '').trim() ? "#ccc" : "#2777AA",
                            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                            fontSize: "16px",
                            fontWeight: "500",
                            letterSpacing: 1,
                          }}
                          type="submit"
                          disabled={jobCreated || props.isSubmitting || !props.isValid || !editorContent || !editorContent.replace(/<[^>]*>/g, '').trim()}
                        >
                          {jobCreated ? "Redirecting..." : props.isSubmitting ? "Submitting..." : "Submit"}
                        </Button>
                      </div>
                    </Grid>
                  </Grid>
                  <Notification
                    message={alertContent}
                    open={alert}
                    severity={alertType}
                    onClose={handleAlert}
                  />
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddJob;

const WorkMode = [
  { id: 1, value: "remote", label: "Work Remotely" },
  { id: 2, value: "office", label: "Work From Office" },
  { id: 3, value: "hybrid", label: "Hybrid" },
];
const allStatus = [
  { id: 1, value: "Active", label: "Active" },
  { id: 2, value: "Full-Filled", label: "Full-Filled" },
  { id: 3, value: "Critical", label: "Critical" },
  { id: 4, value: "Hold", label: "Hold" },
  { id: 5, value: "Cancelled", label: "Cancelled" },
  { id: 6, value: "Partially-Fulfilled", label: "Partially-Fulfilled" },
  { id: 7, value: "Close-No_Client_Response", label: "Close-No Client Response" },
  { id: 8, value: "Close-Unable_To_Fulfill", label: "Close-Unable To Fulfill" },
];
const allCurrency = [
  { id: 1, value: "INR", label: "INR" },
  { id: 2, value: "USD", label: "USD" },
  { id: 3, value: "GBP", label: "GBP" },
];
const budgetUOM = [
  { id: 1, value: "perHour", label: "Per Hour" },
  { id: 2, value: "perMonth", label: "Per Month" },
  { id: 3, value: "perDay", label: "Per Day" },
];